// RigWizard — replace the active character's rig.
//
// Launched from the "Replace Rig" toolbar button rather than a file drop:
// rig replacement is destructive (changes the character's appearance,
// breaks blueprints that reference removed bones), so it MUST be an
// intentional action — not a side-effect of dropping a GLB.
//
// New rigs at character-creation time still come from
// CharacterCreationWizard. This wizard is for *replacing* an existing rig
// on an already-created character.

import React, { useRef, useState } from 'react'
import WizardShell, { inputStyle, labelStyle, tokens } from './WizardShell'
import { getDraft, updateDraft, setPendingRigFile, clearPendingRigFile } from '../../../lib/characterAuthoringStore'
import { getCurrentCharacterId } from '../../../lib/characterState'
import { uploadRig, setMaterialMapping, serverExtractedMaterials } from '../../../lib/assetManagerApi.js'
import { buildMaterialMappingFromGlb } from '../../../lib/materialMappingFromGlb.js'

export default function RigWizard({ onDone, onCancel }) {
  const fileInputRef = useRef(null)
  const [file, setFile] = useState(null)
  const [confirmed, setConfirmed] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  // No auto-open: React 18 StrictMode double-mounts in dev, which would
  // open two file pickers. The "Pick file" button gives the user explicit
  // control and matches the destructive-action posture.

  const onPickFile = e => {
    const f = e.target.files?.[0]
    if (f) setFile(f)
  }

  const submit = async () => {
    if (!file) { setError('Pick a .glb file first'); return }
    if (!confirmed) { setError('Confirm rig replacement first'); return }
    // The character draft is only populated after CharacterCreationWizard.
    // For an existing character loaded into the editor, the canonical
    // source of the active character ID is characterState — fall back to
    // it when the draft is empty.
    const charId = getDraft()?.id || getCurrentCharacterId()
    if (!charId) { setError('No active character'); return }

    setSubmitting(true); setError(null)
    try {
      setPendingRigFile(file)
      const fd = new FormData()
      fd.append('file', file)
      const res = await uploadRig(charId, fd)
      const { url } = res
      // ENG-720: the asset-manager now parses the rig and auto-extracts
      // materials/textures + a material_mapping (with materialIds) server-side.
      // Only derive a mapping client-side when talking to an older backend that
      // didn't extract — otherwise we'd clobber the server's richer mapping with
      // a slots-only one. Best-effort: a failure here must not abort replacement.
      if (!serverExtractedMaterials(res)) {
        try {
          const mapping = buildMaterialMappingFromGlb(await file.arrayBuffer())
          if (mapping.length) await setMaterialMapping(charId, mapping)
        } catch (e) {
          console.warn('[RigWizard] material-mapping build failed:', e?.message ?? e)
        }
      }
      // Use a blob URL as the immediate preview source so the avatar
      // re-renders before the next character refresh fetches the S3 URL.
      const blobUrl = URL.createObjectURL(file)
      // Include the id: App's draft-mirror effect drops rigPath updates whose draft id doesn't match the active character.
      updateDraft({ id: charId, rigPath: url || blobUrl })
      clearPendingRigFile()
      onDone?.({ url })
    } catch (e) {
      setError(String(e.message ?? e))
      setSubmitting(false)
    }
  }

  return (
    <WizardShell
      title="Replace character rig"
      subtitle={file?.name ?? 'No file selected'}
      onCancel={onCancel}
      onSubmit={submit}
      submitDisabled={!file || !confirmed || submitting}
      submitLabel={submitting ? 'Uploading…' : 'Replace rig'}
      error={error}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept=".glb"
        onChange={onPickFile}
        style={{ display: 'none' }}
      />

      <div style={{
        background: 'color-mix(in srgb, var(--m-danger) 12%, transparent)',
        border: '1px solid color-mix(in srgb, var(--m-danger) 55%, transparent)',
        borderRadius: 4, padding: '10px 12px', marginBottom: 14,
        color: 'color-mix(in srgb, var(--m-danger) 75%, var(--m-fg))',
        fontFamily: 'var(--m-font)', fontSize: 11, lineHeight: 1.5,
      }}>
        <strong>This replaces the active character's rig.</strong> Blueprints
        that reference bones removed in the new rig will silently drop their
        bindings. There is no undo — the previous rig.glb is overwritten on
        the server.
      </div>

      <label style={labelStyle}>GLB file</label>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button onClick={() => fileInputRef.current?.click()} style={pickBtn}>
          {file ? 'Pick different file' : 'Pick file'}
        </button>
        <div style={{ color: tokens.dim, fontFamily: 'var(--m-mono)', fontSize: 10, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {file ? `${file.name} (${(file.size/1024/1024).toFixed(2)} MB)` : '—'}
        </div>
      </div>

      <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 16, color: tokens.text, fontFamily: 'var(--m-font)', fontSize: 11, cursor: 'pointer' }}>
        <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} />
        I understand this replaces the rig and may break blueprints.
      </label>
    </WizardShell>
  )
}

const pickBtn = {
  background: 'transparent',
  border: `1px solid ${tokens.border}`,
  color: tokens.text,
  borderRadius: 3,
  padding: '5px 12px',
  fontFamily: 'var(--m-font)', fontSize: 11,
  letterSpacing: '.04em', cursor: 'pointer',
}
